import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { StoragePath } from '../types'
import { apiService } from './api.service'

export class StoragePathService {
  private _listAllCache: Promise<{ results: StoragePath[]; count: number }> | null = null

  async listAll(params: any = {}): Promise<{ results: StoragePath[]; count: number }> {
    if (Object.keys(params).length === 0) {
      if (!this._listAllCache) {
        this._listAllCache = apiService
          .get('storage_paths/', {
            params: { page: 1, page_size: 100000 },
          })
          .then((response) => response.data)
      }
      return this._listAllCache
    } else {
      const mergedParams = { page: 1, page_size: 100000, ...params }
      const response = await apiService.get('storage_paths/', { params: mergedParams })
      return response.data
    }
  }

  clearCache(): void {
    this._listAllCache = null
  }

  // Alias for compatibility
  async getStoragePaths(params: any = {}): Promise<{ results: StoragePath[]; count: number }> {
    return this.listAll(params)
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<{ results: StoragePath[]; count: number }> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true, // Include full permission fields
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    const response = await apiService.get('storage_paths/', { params })
    return response.data
  }

  async getStoragePath(id: number): Promise<StoragePath> {
    const response = await apiService.get(`storage_paths/${id}/`)
    return response.data
  }

  async createStoragePath(data: Partial<StoragePath>): Promise<StoragePath> {
    this.clearCache()
    const response = await apiService.post('storage_paths/', data)
    return response.data
  }

  async updateStoragePath(id: number, data: Partial<StoragePath>): Promise<StoragePath> {
    this.clearCache()
    const response = await apiService.patch(`storage_paths/${id}/`, data)
    return response.data
  }

  async deleteStoragePath(id: number): Promise<void> {
    this.clearCache()
    await apiService.delete(`storage_paths/${id}/`)
  }

  async testPath(path: string, documentId: number): Promise<string> {
    const response = await apiService.post('storage_paths/test/', {
      path,
      document: documentId,
    })
    return response.data
  }

  async bulkResolvePaths(documentIds: number[]): Promise<Record<number, string | null>> {
    const response = await apiService.post('storage_paths/bulk_resolve_paths/', {
      documents: documentIds,
    })
    return response.data
  }

  async getFolderStructure(): Promise<any[]> {
    const response = await apiService.get('storage_paths/folder_structure/')
    return response.data
  }

  // Alias methods for ManagementList compatibility
  async list(
    page: number = 1,
    _pageSize: number = 50,
    sortField: string = 'name',
    sortReverse: boolean = false,
    extraParams: any = {}
  ): Promise<{ results: StoragePath[]; count: number }> {
    return this.listFiltered(page, undefined, sortField, sortReverse, '', true, extraParams)
  }

  async delete(storagePath: StoragePath): Promise<void> {
    return this.deleteStoragePath(storagePath.id)
  }

  async create(data: Partial<StoragePath>): Promise<StoragePath> {
    return this.createStoragePath(data)
  }

  async update(id: number, data: Partial<StoragePath>): Promise<StoragePath> {
    return this.updateStoragePath(id, data)
  }

  // Bulk edit for permissions
  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: any = {
      objects,
      object_type: 'storage_paths',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }
}

export const storagePathService = new StoragePathService()
